/**
 * Column filters: any column, on its value or its percentile.
 *
 * A filter can reference a column that isn't in the section you're looking at —
 * "show me closeout efficiency for players above the 80th in usage" is one view,
 * not two. Since the table endpoint serves one section at a time, that only works
 * because the caller passes the filtered fields as `cols` and the server keeps
 * them in the response; the values are already in its cache entry for the scope.
 * Without that the row has no value for the field and `applyFilters` drops it.
 *
 * Filters live in the URL as `filter=usg~v~gte~25,ts~p~gte~80`. Terse because
 * these strings get pasted around, and `~` doesn't need escaping.
 */

import { PlayerStatColumn, PlayerStatSection } from "../../../models/PlayerStats";

/** `v` filters the raw value, `p` the percentile. */
export type FilterBasis = "v" | "p";
export type FilterOperator = "gte" | "lte";

export interface ColumnFilter {
  field: string;
  basis: FilterBasis;
  op: FilterOperator;
  value: number;
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  gte: "≥",
  lte: "≤",
};

/** Identity columns are filterable too — a games-played floor is the most
 *  common filter there is, and it isn't a stat. */
export interface FilterableColumn {
  field: string;
  label: string;
  section: string;
  /** Identity columns have no percentile, so only value filtering applies. */
  valueOnly?: boolean;
}

export const IDENTITY_FILTER_COLUMNS: FilterableColumn[] = [
  { field: "gp", label: "Games played", section: "Player", valueOnly: true },
  { field: "poss", label: "Possessions", section: "Player", valueOnly: true },
];

/** These live on the row itself, not in `v`, so they never need requesting. */
export const IDENTITY_FILTER_FIELDS = new Set(
  IDENTITY_FILTER_COLUMNS.map((c) => c.field),
);

export const POSITIONS = ["ALL", "G", "W", "F", "C"];

/**
 * Two kinds of control, and conflating them is a bug waiting to happen:
 *
 * - **Row selection** — which rows you see. Search, team.
 * - **Population definition** — who you're measured against. Position, column
 *   filters.
 *
 * Search has always been row-selection-only: typing a name mustn't redefine the
 * group someone is ranked against, or percentiles would drift as you type and
 * settle at 50 once one row is left. On the player page team is the same, because
 * there is one player and team can only mean "which of his seasons to show".
 *
 * The league table is the exception, deliberately: filtering it to BOS and ranking
 * within BOS is a coherent question, so team is population-defining there.
 */
export interface PopulationFilters {
  position: string;
  columnFilters: ColumnFilter[];
}

/** Rows of a population that survive the population-defining controls. */
export function applyPopulationFilters<
  T extends FilterableRow & { pos?: string | null },
>(rows: T[], { position, columnFilters }: PopulationFilters): T[] {
  const scoped =
    position && position !== "ALL"
      ? rows.filter((row) => row.pos === position)
      : rows;
  return applyFilters(scoped, columnFilters);
}

/**
 * @param currentSection the section being viewed, hoisted to the top of the list
 *   (below the identity columns). Every section stays available — filtering on a
 *   column you're not looking at is the point — but the ones on screen are the
 *   ones you're most likely to want, so they shouldn't be four groups down.
 */
export function filterableColumns(
  sections: PlayerStatSection[],
  currentSection?: string | null,
): FilterableColumn[] {
  const ordered = currentSection
    ? [
        ...sections.filter((s) => s.key === currentSection),
        ...sections.filter((s) => s.key !== currentSection),
      ]
    : sections;

  const stats = ordered.flatMap((section) =>
    section.columns
      .filter((column: PlayerStatColumn) => column.format !== "text")
      .map((column) => ({
        field: column.key,
        label: `${column.group} · ${column.label}`,
        section: section.label,
      })),
  );
  return [...IDENTITY_FILTER_COLUMNS, ...stats];
}

export function encodeFilters(filters: ColumnFilter[]): string {
  // A row still being filled in encodes with an empty value rather than being
  // dropped. The URL is the single source of truth for the filter list, so
  // dropping it here would delete the row the instant it was added.
  return filters
    .map(
      (f) =>
        `${f.field}~${f.basis}~${f.op}~${Number.isFinite(f.value) ? f.value : ""}`,
    )
    .join(",");
}

export function decodeFilters(encoded: string | null): ColumnFilter[] {
  if (!encoded) return [];
  return encoded
    .split(",")
    .map((part) => {
      const [field, basis, op, value] = part.split("~");
      if (!field || !basis || !op) return null;
      // `Number("")` is 0, so the empty case has to be checked before coercing.
      return {
        field,
        basis: basis === "p" ? "p" : "v",
        op: op === "lte" ? "lte" : "gte",
        value: value === "" || value === undefined ? NaN : Number(value),
      } as ColumnFilter;
    })
    .filter((f): f is ColumnFilter => f !== null);
}

export interface FilterableRow {
  v: Record<string, number>;
  p: Record<string, number>;
  gp: number | null;
  poss: number | null;
}

function readField(row: FilterableRow, filter: ColumnFilter): number | null {
  if (filter.basis === "v") {
    if (filter.field === "gp") return row.gp;
    if (filter.field === "poss") return row.poss;
    const value = row.v?.[filter.field];
    return typeof value === "number" ? value : null;
  }
  const percentile = row.p?.[filter.field];
  return typeof percentile === "number" ? percentile : null;
}

/**
 * The filters that actually narrow the table. A row whose value hasn't been
 * typed yet is inert, not a filter on zero — it exists so the panel has
 * something to render while you fill it in.
 *
 * Shared with the toolbar badge, whose job is to account for missing rows: it
 * would be lying if it counted a row that hides nothing.
 */
export function activeFilters(filters: ColumnFilter[]): ColumnFilter[] {
  return filters.filter((f) => Number.isFinite(f.value));
}

export function applyFilters<T extends FilterableRow>(
  rows: T[],
  filters: ColumnFilter[],
): T[] {
  const active = activeFilters(filters);
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every((filter) => {
      const value = readField(row, filter);
      // A player with no post-ups has no post efficiency — that isn't a zero,
      // and it shouldn't sneak past a "≤" filter as though it were.
      if (value == null) return false;
      return filter.op === "gte" ? value >= filter.value : value <= filter.value;
    }),
  );
}
